import { calcularSalario, calcularValor } from './financas';
import { overallBase } from './gerador';
import type { Rng } from './rng';
import type { Clube, Jogador, Posicao, Trofeu } from './tipos';

/**
 * Contratos, multa de rescisão, empréstimo bancário, academia de juniores e troféus.
 * Regras do original em ../../../conhecimento/12 e 13 (a.ds, best.^C, a.jc, best.^F.fY/ga/gf).
 */

export const NOME_LIGA = 'Brasileirão Série A 2026';
export const NOME_SERIE_B = 'Brasileirão Série B 2026';

// ---- Contratos e multa de rescisão ----------------------------------------------------------------

export const DIAS_POR_RODADA = 7;
export const CONTRATO_NOVO_DIAS = 730;

/** Contrato inicial determinístico (60 a 1100 dias), sem consumir o gerador aleatório. */
export function contratoInicial(id: number): number {
  return 60 + ((id * 37) % 1041);
}

/** Percentual do valor do jogador cobrado do clube que o vende, conforme os dias de contrato restantes (a.jc do original). */
export function percentualMulta(dias: number): number {
  if (dias <= 0) return 0;
  if (dias <= 30) return 12;
  if (dias <= 60) return 20;
  if (dias <= 90) return 22;
  if (dias <= 180) return 25;
  if (dias <= 722) return 30;
  return 35;
}

export function multaRescisao(j: Jogador): number {
  return Math.round((j.valor * percentualMulta(j.contratoDias ?? 0)) / 100);
}

/** Desconta 7 dias de todos os contratos do clube; contratos vencidos são renovados com salário recalculado. Devolve os renovados. */
export function passarDiasDeContrato(c: Clube): Jogador[] {
  const renovados: Jogador[] = [];
  for (const j of c.elenco) {
    const restante = (j.contratoDias ?? contratoInicial(j.id)) - DIAS_POR_RODADA;
    if (restante > 0) {
      j.contratoDias = restante;
      continue;
    }
    j.contratoDias = 365 * (1 + (j.id % 3));
    j.salario = calcularSalario(j.overall, j.idade, j.posicao, c.nivel);
    renovados.push(j);
  }
  return renovados;
}

// ---- Empréstimo bancário -------------------------------------------------------------------------

export const PARCELA_EMPRESTIMO = 500_000;
/** Limite por divisão (best.^C.m): índice 1 = elite. */
const LIMITE_EMPRESTIMO = [1_000_000, 5_000_000, 3_000_000, 2_000_000, 1_500_000];
export const JUROS_MENSAIS_PCT = 3;

export function limiteEmprestimo(divisao = 1): number {
  return (divisao >= 1 && divisao <= 4 ? LIMITE_EMPRESTIMO[divisao] : LIMITE_EMPRESTIMO[0]) as number;
}

export function jurosMensais(c: Clube): number {
  return Math.round(((c.emprestimo ?? 0) * JUROS_MENSAIS_PCT) / 100);
}

export function pegarEmprestimo(c: Clube, divisao = 1): void {
  if ((c.emprestimo ?? 0) >= limiteEmprestimo(divisao)) throw new Error('empréstimo bancário - limite máximo alcançado');
  c.emprestimo = (c.emprestimo ?? 0) + PARCELA_EMPRESTIMO;
  c.caixa += PARCELA_EMPRESTIMO;
}

export function pagarEmprestimo(c: Clube): void {
  if ((c.emprestimo ?? 0) <= 0) throw new Error('não há dívida');
  if (c.caixa < PARCELA_EMPRESTIMO) throw new Error('caixa insuficiente para pagar a parcela');
  c.emprestimo = (c.emprestimo ?? 0) - PARCELA_EMPRESTIMO;
  c.caixa -= PARCELA_EMPRESTIMO;
}

/** Cobra os juros do mês (3% da dívida). */
export function cobrarJuros(c: Clube): number {
  const j = jurosMensais(c);
  if (j > 0) {
    c.caixa -= j;
    c.balanco.juros = (c.balanco.juros ?? 0) + j;
  }
  return j;
}

// ---- Academia de juniores ------------------------------------------------------------------------

export const LIMITE_JUNIORES = 20;
export const JUNIORES_INICIAIS = 10;

/** Talento mínimo para a promoção automática da CPU, por reputação 0..5 (best.^F.gf). */
const TALENTO_PARA_PROMOVER = [1, 4, 5, 6, 6, 6];
/** Quantos jogadores o clube quer na posição antes de deixar de promover (G, L, Z, M, A). */
const PROFUNDIDADE_DESEJADA: Record<Posicao, number> = { G: 3, L: 5, Z: 5, M: 8, A: 6 };

function sortearTalento(nivel: number, reputacao: number, rng: Rng): number {
  const r = rng.faixa(1, 100);
  const tabela: [number, number][] =
    nivel < 19 && reputacao <= 3
      ? nivel >= 15
        ? [[2, 1], [5, 2], [10, 4], [30, 5], [65, 6], [90, 7], [95, 8], [98, 9], [100, 10]]
        : [[4, 1], [8, 2], [15, 3], [25, 4], [50, 5], [75, 6], [95, 7], [98, 8], [99, 9], [100, 10]]
      : [[2, 1], [5, 2], [10, 4], [25, 5], [60, 6], [80, 7], [90, 8], [98, 9], [100, 10]];
  for (const [limite, t] of tabela) if (r <= limite) return t;
  return 10;
}

function sortearPosicaoJunior(rng: Rng): Posicao {
  const r = rng.faixa(1, 100);
  if (r <= 10) return 'G';
  if (r <= 30) return 'L';
  if (r <= 50) return 'Z';
  if (r <= 80) return 'M';
  return 'A';
}

const CARAC_POR_POSICAO: Record<Posicao, number[]> = { G: [0, 1, 2, 3], Z: [7, 5, 10, 12, 13], L: [10, 6, 11, 9, 13], M: [4, 11, 7, 9, 12], A: [9, 5, 13, 8, 11] };

export function gerarJunior(id: number, clube: Clube, rng: Rng, posicao?: Posicao): Jogador {
  const pos = posicao ?? sortearPosicaoJunior(rng);
  const talento = sortearTalento(clube.nivel, clube.reputacao, rng);
  const idade = rng.faixa(16, 19);
  const overall = Math.round(Math.min(90, Math.max(15, overallBase(clube.nivel) - 12 + talento * 1.8 + rng.normal(0, 2))));
  const baseDesenv: Record<number, number> = { 16: 15, 17: 35, 18: 55, 19: 70 };
  const desenvolvimento = Math.min(95, (baseDesenv[idade] as number) + rng.faixa(1, 5) + talento);
  const lista = CARAC_POR_POSICAO[pos];
  const a = rng.escolher(lista);
  const b = rng.escolher(lista.filter((x) => x !== a));
  return {
    id,
    nome: '',
    idade,
    posicao: pos,
    overall,
    energia: 100,
    // base paga bem menos que o profissional: o salário e o valor cheios só valem depois da promoção
    salario: Math.round((calcularSalario(overall, idade, pos, clube.nivel) * 0.25) / 100) * 100,
    valor: Math.round((calcularValor(overall, idade, clube.nivel, pos) * 0.5) / 1000) * 1000,
    lesaoRodadas: 0,
    suspensaoRodadas: 0,
    amarelosAcumulados: 0,
    gols: 0,
    jogos: 0,
    assistencias: 0,
    notaSoma: 0,
    lado: rng.chance(0.5) ? 'E' : 'D',
    caracteristicas: [a, b],
    talento,
    desenvolvimento,
    contratoDias: 365 * 3,
  };
}

/** Evolução semanal dos juniores com até 20 anos (best.^F.fY). */
export function evoluirJuniores(c: Clube): void {
  for (const j of c.juniores ?? []) {
    if (j.idade > 20) continue;
    const t = j.talento ?? 5;
    let ganho = j.idade <= 17 ? 20 / 40 : j.idade === 18 ? 15 / 40 : j.idade === 19 ? 14 / 40 : 5 / 40;
    ganho += t <= 3 ? 0.03 : t <= 6 ? 0.04 : t <= 8 ? 0.07 : t === 9 ? 0.1 : 0.11;
    j.desenvolvimento = Math.min(100, (j.desenvolvimento ?? 0) + ganho);
  }
}

/** Ao subir para o profissional, o desenvolvimento acumulado vira força: até +talento×0,6 com a base 100% pronta. */
function prepararPromocao(j: Jogador, c: Clube): void {
  const bonus = Math.round(((j.desenvolvimento ?? 0) / 100) * (j.talento ?? 5) * 0.6);
  j.overall = Math.min(92, j.overall + bonus);
  j.salario = calcularSalario(j.overall, j.idade, j.posicao, c.nivel);
  j.valor = calcularValor(j.overall, j.idade, c.nivel, j.posicao);
  j.contratoDias = 365 * 3;
  delete j.talento;
  delete j.desenvolvimento;
}

export function promoverJunior(c: Clube, jogadorId: number, elencoMax: number): Jogador {
  const lista = c.juniores ?? [];
  const j = lista.find((x) => x.id === jogadorId);
  if (!j) throw new Error('junior não encontrado');
  if (c.elenco.length >= elencoMax) throw new Error('não há vagas no time profissional');
  c.juniores = lista.filter((x) => x.id !== jogadorId);
  prepararPromocao(j, c);
  c.elenco.push(j);
  return j;
}

export function dispensarJunior(c: Clube, jogadorId: number): void {
  c.juniores = (c.juniores ?? []).filter((x) => x.id !== jogadorId);
}

export function definirApelido(j: Jogador, apelido: string): void {
  const limpo = apelido.trim();
  if (limpo.length < 2) throw new Error('apelido muito curto');
  if (limpo.length > 24) throw new Error('apelido muito longo');
  if (j.nomeCompleto === undefined) j.nomeCompleto = j.nome;
  j.nome = limpo;
}

/** Preço que um clube cobra por um junior seu (valor × (1 + talento/10)). */
export function precoJunior(j: Jogador): number {
  return Math.round((j.valor * (1 + (j.talento ?? 5) / 10)) / 1000) * 1000;
}

export function comprarJunior(comprador: Clube, vendedor: Clube, jogadorId: number): number {
  const j = (vendedor.juniores ?? []).find((x) => x.id === jogadorId);
  if (!j) throw new Error('junior não encontrado');
  if ((comprador.juniores ?? []).length >= LIMITE_JUNIORES) throw new Error('limite de juniores alcançado');
  const preco = precoJunior(j);
  if (comprador.caixa < preco) throw new Error('sem dinheiro para comprar este junior');
  vendedor.juniores = (vendedor.juniores ?? []).filter((x) => x.id !== jogadorId);
  comprador.juniores = [...(comprador.juniores ?? []), j];
  comprador.caixa -= preco;
  vendedor.caixa += preco;
  comprador.balanco.compras = (comprador.balanco.compras ?? 0) + preco;
  comprador.balanco.transferencias -= preco;
  vendedor.balanco.vendas = (vendedor.balanco.vendas ?? 0) + preco;
  vendedor.balanco.transferencias += preco;
  return preco;
}

/** Cria juniores até `alvo` (usado na criação do jogo e na reposição anual). */
export function completarJuniores(c: Clube, alvo: number, rng: Rng, proximoId: { valor: number }, nomear: (rng: Rng) => string): void {
  const lista = (c.juniores ??= []);
  while (lista.length < alvo && lista.length < LIMITE_JUNIORES) {
    const j = gerarJunior(proximoId.valor++, c, rng);
    j.nome = nomear(rng);
    lista.push(j);
  }
}

/** Virada de temporada: juniores envelhecem; na CPU quem tem 20+ anos sobe (talento e necessidade de posição) ou sai; depois repõe. */
export function virarAnoJuniores(c: Clube, rng: Rng, proximoId: { valor: number }, nomear: (rng: Rng) => string, elencoMax: number): void {
  for (const j of c.juniores ?? []) j.idade++;
  if (!c.humano) {
    for (const j of [...(c.juniores ?? [])]) {
      if (j.idade < 20) continue;
      const precisa = c.elenco.filter((x) => x.posicao === j.posicao).length < PROFUNDIDADE_DESEJADA[j.posicao];
      if ((j.talento ?? 0) >= (TALENTO_PARA_PROMOVER[c.reputacao] ?? 6) && precisa && c.elenco.length < elencoMax) promoverJunior(c, j.id, elencoMax);
      else dispensarJunior(c, j.id);
    }
    completarJuniores(c, JUNIORES_INICIAIS, rng, proximoId, nomear);
  }
}

// ---- Troféus ------------------------------------------------------------------------------------

export function registrarTrofeu(c: Clube, t: Trofeu): void {
  // o título é dado assim que o campeonato termina; o encerramento da temporada repete a chamada e não duplica
  if ((c.trofeus ??= []).some((x) => x.competicao === t.competicao && x.temporada === t.temporada)) return;
  c.trofeus.push(t);
}

/** Agrupa os troféus para a galeria: liga/estadual por divisão, o restante somado (como em a.de do original). */
export interface GrupoTrofeu {
  chave: string;
  competicao: string;
  tipo: Trofeu['tipo'];
  divisao: number;
  quantidade: number;
  temporadas: number[];
}

export function agruparTrofeus(lista: Trofeu[]): GrupoTrofeu[] {
  const mapa = new Map<string, GrupoTrofeu>();
  for (const t of lista) {
    const chave = `${t.tipo}|${t.competicao}|${t.tipo === 'nacional' || t.tipo === 'estadual' ? t.divisao : 0}`;
    const g = mapa.get(chave) ?? { chave, competicao: t.competicao, tipo: t.tipo, divisao: t.divisao, quantidade: 0, temporadas: [] };
    g.quantidade++;
    g.temporadas.push(t.temporada);
    mapa.set(chave, g);
  }
  return [...mapa.values()].sort((a, b) => a.divisao - b.divisao || a.competicao.localeCompare(b.competicao, 'pt-BR'));
}

/** Online: confiança (a menor entre diretoria e torcida) em que o técnico recebe o aviso e em que é demitido. */
export const CONFIANCA_AVISO = 15;
export const CONFIANCA_DEMISSAO = 10;
